import React, { useState, useEffect } from 'react';
import { Send, Wallet, Eye, Edit3 } from 'lucide-react';
import { countMentions } from './commentService';
import MarkdownPreview from './MarkdownPreview';

const CommentInput = ({
  user,
  onSubmit,
  onLogin,
  placeholder = "Add a comment...",
  autoFocus = false,
  onCancel = null,
  isReply = false,
  text: controlledText,        // Optional: lifted state from parent
  onTextChange: controlledSetText  // Optional: lifted state setter from parent
}) => {
  // Use controlled state if provided, otherwise use local state
  const [localText, setLocalText] = useState('');
  const text = controlledText !== undefined ? controlledText : localText;
  const setText = controlledSetText || setLocalText;

  const [submitting, setSubmitting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Auto-switch back to write mode when text becomes empty
  useEffect(() => {
    if (!text.trim() && showPreview) {
      setShowPreview(false);
    }
  }, [text, showPreview]);

  const handleSubmit = async () => {
    if (!text.trim() || submitting) return;

    // Validate mention count before submitting
    const mentionCount = countMentions(text.trim(), user?.address || '');
    if (mentionCount > 100) {
      alert(`This comment mentions ${mentionCount} addresses. Maximum allowed is 100 unique mentions.`);
      return;
    }

    setSubmitting(true);
    try {
      await onSubmit(text.trim());
      setText('');
      setShowPreview(false); // Reset to write mode after successful submit
      if (onCancel) onCancel(); // Close reply input after submitting
    } catch (error) {
      console.error('Error submitting comment:', error);
      alert('Failed to post comment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
  };

  if (!user) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
        <button
          onClick={onLogin}
          className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 transition font-medium text-sm"
        >
          <Wallet className="w-4 h-4" />
          Please Login to Comment
        </button>
      </div>
    );
  }

  return (
    <div className={`bg-white border border-gray-200 rounded-lg ${isReply ? '' : 'p-4'}`}>
      {/* Tab buttons for Write/Preview */}
      <div className="flex border-b border-gray-200 mb-2">
        <button
          onClick={() => setShowPreview(false)}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border-b-2 transition ${
            !showPreview
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Edit3 className="w-3.5 h-3.5" />
          Write
        </button>
        <button
          onClick={() => setShowPreview(true)}
          disabled={!text.trim()}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border-b-2 transition ${
            showPreview
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 disabled:text-gray-300 disabled:cursor-not-allowed'
          }`}
        >
          <Eye className="w-3.5 h-3.5" />
          Preview
        </button>
      </div>

      {/* Write mode */}
      {!showPreview ? (
        <>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            autoFocus={autoFocus}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 resize-none"
            rows={isReply ? 2 : 3}
            maxLength={2000}
            disabled={submitting}
          />
          <div className="text-xs text-gray-400 mt-1">
            Supports <a href="https://markdownlivepreview.com/" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-600 hover:underline">Markdown</a> formatting
          </div>
        </>
      ) : (
        /* Preview mode */
        <div
          className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 overflow-auto prose prose-sm max-w-none"
          style={{ minHeight: isReply ? '60px' : '84px', maxHeight: '300px' }}
        >
          <MarkdownPreview text={text} />
        </div>
      )}

      <div className="flex items-center justify-between mt-2">
        <span className="text-xs text-gray-500">
          {text.length}/2000 characters
          {text.length > 0 && !showPreview && <span className="ml-2">• Cmd/Ctrl + Enter to post</span>}
        </span>

        <div className="flex items-center gap-2">
          {onCancel && (
            <button
              onClick={onCancel}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition"
              disabled={submitting}
            >
              Cancel
            </button>
          )}

          <button
            onClick={handleSubmit}
            disabled={!text.trim() || submitting}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition text-sm font-medium"
          >
            {submitting ? (
              <>
                <span className="animate-spin">⏳</span>
                Posting...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Post
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommentInput;
